import { formatDate, TitleCasePipe } from '@angular/common';
import { translate } from '@jsverse/transloco';
import { ActivityDetails, Stage } from 'src/app/models/instance/activity';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { InstancesService } from 'src/app/services/api/instances.service';
import { ActivityProgressService } from 'src/app/services/util/activity-progress.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { finalize } from 'rxjs/operators';
import { toUtcString, formatDateTime, calculateDuration } from 'src/app/services/util/utils';


@Component({
  selector: 'app-activity-left-panel',
  templateUrl: './activity-left-panel.component.html',
  styleUrls: ['./activity-left-panel.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ActivityLeftPanelComponent implements OnInit, OnDestroy{
  @Input() data: any;
  @Input() activityDetails!: ActivityDetails | undefined;
  @Input() isDataLoading = true;
  @Input() isRefreshing = false;
  @Input() selectedStage: Stage | undefined;
  @Output() stageSelected = new EventEmitter<any>();
  @Output() stageTitleSelected = new EventEmitter<string>();
  @Output() aborted = new EventEmitter<void>();
  isAborting = false;
  inprogressStatus = ['inprogress'];
  failedStatus = ['failed'];
  completedStatus = ['success'];
  stageTypeText: Record<string, string> = {
    agent_deployment: "Agent Deployment",
    component_execution: "Component Execution"
  };
  currentTime: number = Date.now();
  statusIcon = {failed: 'warning_amber', success: 'check_circle_filled'};
  formatDateTime = formatDateTime;
  toUtcString = toUtcString;
  calculateDuration = calculateDuration;
  progressTimer!: ReturnType<typeof setInterval>;

  constructor(
    public activityProgressService: ActivityProgressService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private instancesService: InstancesService,
    private toastrService: CustomToastrService,
    private destroyRef: DestroyRef
  ) {}

  private normalizeDate(dateStr: string | Date | null | undefined): string | null {
    if (!dateStr) return null;
    return toUtcString(dateStr as string);
  }

  // Initialize activity data and start 1-second update interval for live calculations
  ngOnInit(): void {
    this.progressTimer = setInterval(() => {
      this.currentTime = Date.now();
      this.cdr.markForCheck();
    }, 1000);
  }

  onStageClick(stage: Stage): void {
    this.selectedStage = stage;
    this.stageSelected.emit(stage);

    const stageIndex = (this.activityDetails?.stages ?? []).indexOf(stage);
    this.stageTitleSelected.emit(`Stage ${stageIndex + 1} - ${this.stageTypeText[stage.type] || stage.type}`);
  }

  getTooltipText(stage: Stage): string {
    const start = formatDate(stage?.startTime, 'MMM d, y, H:mm:ss', 'en-US');
    if (!stage?.finishTime) return translate('INSTANCE_LIST.START_TIME') + ': ' + start;
    const end = formatDate(stage?.finishTime, 'MMM d, y, H:mm:ss', 'en-US');
    return translate('INSTANCE_LIST.START_TIME') + ': ' + start + ' ' + translate('INSTANCE_LIST.END_TIME') + ': ' + end;
  }
  // Calculate expected completion time based on start time and expected duration
  get expectedEndTime(): string {
    const startTime = this.normalizeDate(this.activityDetails?.startTime);
    if (!this.activityDetails?.expectedDurationMs || !startTime) { return '--'; }
    const startMs = new Date(startTime).getTime();
    if (isNaN(startMs)) { return '--'; }
    const expectedTime = new Date(startMs + Number(this.activityDetails?.expectedDurationMs));
    if (isNaN(expectedTime.getTime())) { return '--'; }
    return formatDateTime(expectedTime.toISOString());
  }

  // Format expected duration in "Xh Ym Zs" format
  get expectedDuration(): string {
    const durationMs = Number(this.activityDetails?.expectedDurationMs);
    if (!durationMs || !Number.isFinite(durationMs)) { return '--'; }
    return this.activityProgressService.formatDuration(durationMs);
  }

  // Calculate delay duration if activity finished after expected end time
  get delayedDuration(): string | null {
    const startTime = this.normalizeDate(this.activityDetails?.startTime);
    const finishTime = this.normalizeDate(this.activityDetails?.finishTime);
    if (!finishTime || !startTime || !this.activityDetails?.expectedDurationMs) { return null; }
    const startMs = new Date(startTime).getTime();
    const finishMs = new Date(finishTime).getTime();
    if (isNaN(startMs) || isNaN(finishMs)) { return null; }
    const expectedEndMs = startMs + Number(this.activityDetails?.expectedDurationMs);
    const delayMs = finishMs - expectedEndMs;
    if (delayMs <= 0) { return null; }
    const formatted = this.activityProgressService.formatDuration(delayMs);
    return formatted === '—' ? null : formatted;
  }

  // Calculate remaining time until expected completion
  get timeRemaining(): string {
    const startTime = this.normalizeDate(this.activityDetails?.startTime);
    return startTime ? this.activityProgressService.timeDifference(startTime, Number(this.activityDetails?.expectedDurationMs), this.currentTime) : '';
  }

  // Calculate progress percentage (0-100) based on elapsed vs expected time
  get progressPercent(): number {
    const startTime = this.normalizeDate(this.activityDetails?.startTime);
    return startTime ? this.activityProgressService.getElapsedPercent(startTime, Number(this.activityDetails?.expectedDurationMs), this.currentTime) : 0;
  }

  // Format elapsed time for progress bar tooltip
  get elapsedTimeDisplay(): string {
    const startTime = this.normalizeDate(this.activityDetails?.startTime);
    if (!startTime) return '--';
    const elapsedTime = this.activityProgressService.getElapsedTime(startTime, this.currentTime);
    return elapsedTime !== '—' ? `${elapsedTime}` : '--';
  }

  // Get progress bar tooltip text based on threshold reached status
  get progressBarTooltip(): string {
    const isThresholdReached = this.progressPercent >= 100 && this.activityDetails?.status === 'inprogress';
    const prefix = isThresholdReached ? translate('ACTIVITY_DETAILS.THRESHOLD_REACHED') : translate('ACTIVITY_DETAILS.NOW');
    return `${prefix} · ${this.elapsedTimeDisplay}`;
  }

  // Clean up interval timer on component destroy
  ngOnDestroy(): void {
    if (this.progressTimer) clearInterval(this.progressTimer);
  }

  //Abort cannot be undone, so the user must confirm before the API call.
  onAbortClick(): void {
    if (this.isAborting) {
      return;
    }
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('INSTANCE_DETAILS.ABORT'),
        message: translate('INSTANCE_DETAILS.ABORT_MSG', { activity: new TitleCasePipe().transform(this.activityDetails?.operation) ?? '' }),
        confirmButtonText: translate('BUTTONS.CONFIRM')
      }
    }).afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
        this.isAborting = true;
        const forceAbort = this.activityProgressService.calculateForceAbort(this.activityDetails);
        this.instancesService.putInstanceAbort(this.data?.instanceId, forceAbort)
        .pipe(
          finalize(() => (this.isAborting = false)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: () => this.aborted.emit(),
          error: (error: { message?: string; error?: { message?: string }; errmsg?: string }) => {
            const message = error?.message ?? error?.error?.message ?? error?.errmsg ?? translate('SERVICE.INTERNAL_SERVER_ERROR');
            this.toastrService.errMessage(message, translate('ERRORS.SERVICE_AVAILABILITY'));
          }
        });
      }
    });
  }
}